import type { QueryKey } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { validate as isUuid } from "uuid";

import { isConnectionFailure } from "@/api/connection.types";
import { labAssetQueryKeys } from "@/Features/LabAssets/labAssets.queryKeys";
import { toast } from "@/utils/toastService";

import { AssetValidationsAPI } from "./asset-validations.api";
import type {
  AssetValidation,
  CreateAssetValidationBody,
  QueryAssetValidationsParams,
  UpdateAssetValidationBody,
} from "./asset-validations.types";

/**
 * Query-key factory for the validations table (AssetValidationsTable).
 * Mirrors `labAssetQueryKeys`. The create modal (ENG-999) and edit flyout
 * (ENG-1000) invalidate `infiniteLists` after a mutation so every asset's
 * table refetches on its next mount.
 */
export const assetValidationQueryKeys = {
  all: ["asset-validations"] as QueryKey,
  infiniteLists: ["asset-validations", "infinite-list"] as QueryKey,
  infiniteList: (query: QueryAssetValidationsParams): QueryKey => [
    "asset-validations",
    "infinite-list",
    query,
  ],
  item: (uuid?: string): QueryKey => ["asset-validations", "item", { uuid }],
};

/**
 * The asset row's `next_validation_due` is derived from its validations
 * (ENG-1679), so every validation mutation also invalidates the asset item
 * and the assets lists (badge + filter, ENG-1680). Called from the create
 * and update hooks below and from DeleteValidationModal.
 */
export const invalidateLabAssetForValidation = (
  queryClient: ReturnType<typeof useQueryClient>,
  asset_uuid: string | undefined
) => {
  queryClient.invalidateQueries({
    queryKey: labAssetQueryKeys.item(asset_uuid),
  });
  queryClient.invalidateQueries({
    queryKey: labAssetQueryKeys.infiniteLists,
  });
};

const GENERIC_LOAD_ERROR =
  "An error occurred while loading this validation. Please try again.";

export type UseAssetValidationItemOptions = {
  /**
   * Seed for an instant paint (the flyout passes its table row). Pair with
   * `initialDataUpdatedAt: 0` so the seed counts as stale and the refetch on
   * mount still happens under any staleTime.
   */
  initialData?: AssetValidation;
  initialDataUpdatedAt?: number;
};

/**
 * The single owner of the `assetValidationQueryKeys.item(uuid)` cache entry
 * (the flyout, ENG-1000). Same shape and 404 policy as useLabAssetItem: a
 * real row passes through, a true 404 maps to null (the not-found signal),
 * every other failure throws so it surfaces as isError instead of being
 * cached as a not-found. A malformed uuid resolves to null without a
 * request; the api would 400 it forever.
 */
export const useAssetValidationItem = (
  uuid: string | undefined,
  options: UseAssetValidationItemOptions = {}
) =>
  useQuery({
    queryKey: assetValidationQueryKeys.item(uuid),
    queryFn: async (): Promise<AssetValidation | null> => {
      if (!uuid || !isUuid(uuid)) return null;
      const response: unknown = await AssetValidationsAPI.getById(uuid, {
        skipErrorToast: true,
      });
      if (
        response &&
        typeof response === "object" &&
        "uuid" in response &&
        (response as AssetValidation).uuid
      ) {
        return response as AssetValidation;
      }
      const failure = isConnectionFailure(response)
        ? response.error?.response
        : undefined;
      if (failure?.status === 404) return null;
      if (failure?.status !== 401) {
        toast.error(failure?.data?.message || GENERIC_LOAD_ERROR);
      }
      throw new Error(`Failed to load validation ${uuid}`);
    },
    retry: false,
    enabled: !!uuid,
    ...options,
  });

const GENERIC_CREATE_ERROR =
  "An error occurred while creating this validation. Please try again.";

/**
 * POST /validations. Modeled on useUpdateLabAsset (labAssets.query.ts).
 *
 * connection.js resolves HTTP failures as { success: false, error } instead
 * of rejecting, so the mutationFn re-throws them as an Error carrying the
 * server's message when it sends one (a 400 "Unknown asset_uuid", a 403) so
 * they surface through the mutation's error path rather than as a success.
 * The request opts out of the interceptor toast; the caller owns the single
 * error snackbar.
 *
 * The response is the created row. onSuccess invalidates the infinite lists
 * so the asset's validations table refetches; nothing else caches
 * validations yet.
 */
export const useCreateAssetValidation = () => {
  const queryClient = useQueryClient();

  return useMutation<AssetValidation, Error, CreateAssetValidationBody>({
    mutationFn: async (body) => {
      const result: unknown = await AssetValidationsAPI.create(body, {
        skipErrorToast: true,
      });
      if (!result || isConnectionFailure(result)) {
        const data = isConnectionFailure(result)
          ? result.error?.response?.data
          : undefined;
        throw new Error(data?.message || data?.error || GENERIC_CREATE_ERROR);
      }
      return result as AssetValidation;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({
        queryKey: assetValidationQueryKeys.infiniteLists,
      });
      invalidateLabAssetForValidation(queryClient, row.asset_uuid);
    },
  });
};

const GENERIC_UPDATE_ERROR =
  "An error occurred while updating this validation. Please try again.";

/**
 * Thrown by useUpdateAssetValidation for a failed PATCH. `status` is the HTTP
 * status when the server answered (undefined for a network failure) so the
 * hook can tell a 404 (row deleted mid-edit) from a dropped connection.
 */
export class AssetValidationUpdateError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "AssetValidationUpdateError";
    this.status = status;
  }
}

export type UpdateAssetValidationVariables = {
  uuid: string;
  body: UpdateAssetValidationBody;
};

/**
 * PATCH /validations/:uuid. Same failure handling as useCreateAssetValidation
 * (the caller owns the single error snackbar). The response is the updated
 * row, so onSuccess writes it straight into the item cache (the flyout
 * re-renders without a refetch) and invalidates the infinite lists so the
 * table refetches. On a 404 the row is gone: invalidate the item so
 * useAssetValidationItem renders not-found instead of the stale snapshot.
 */
export const useUpdateAssetValidation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AssetValidation,
    AssetValidationUpdateError,
    UpdateAssetValidationVariables
  >({
    mutationFn: async ({ uuid, body }) => {
      const result: unknown = await AssetValidationsAPI.update(uuid, body, {
        skipErrorToast: true,
      });
      if (!result || isConnectionFailure(result)) {
        const failure = isConnectionFailure(result)
          ? result.error?.response
          : undefined;
        throw new AssetValidationUpdateError(
          failure?.data?.message ||
            failure?.data?.error ||
            GENERIC_UPDATE_ERROR,
          failure?.status
        );
      }
      return result as AssetValidation;
    },
    onSuccess: (row, variables) => {
      queryClient.setQueryData(
        assetValidationQueryKeys.item(variables.uuid),
        row
      );
      queryClient.invalidateQueries({
        queryKey: assetValidationQueryKeys.infiniteLists,
      });
      invalidateLabAssetForValidation(queryClient, row.asset_uuid);
    },
    onError: (error, variables) => {
      if (error.status === 404) {
        queryClient.invalidateQueries({
          queryKey: assetValidationQueryKeys.item(variables.uuid),
        });
      }
    },
  });
};
